import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Trash2, Plus, Image as ImageIcon, MessageSquareQuote } from "lucide-react";

const emptyPhoto = { title: "", file: null };
const emptyTestimonial = { name: "", destination: "", testimonial: "" };

export default function AdminGlimpsesPage() {
  const { user, loading } = useAuth();
  const [items, setItems] = useState([]);
  const [photo, setPhoto] = useState(emptyPhoto);
  const [testimonial, setTestimonial] = useState(emptyTestimonial);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/glimpses");
      setItems(data);
    } catch {
      toast.error("Could not load Glimpses");
    }
  };

  useEffect(() => { if (user?.is_admin) refresh(); }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <div className="max-w-md mx-auto px-6 py-24 text-center text-muted-foreground">Admin access only.</div>;

  const addPhoto = async (e) => {
    e.preventDefault();
    if (!photo.file) return;
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("file", photo.file);
      const { data } = await api.post("/admin/glimpses/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.post("/admin/glimpses", { type: "photo", title: photo.title, image_url: data.image_url });
      setPhoto(emptyPhoto);
      toast.success("Photo added");
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add photo"); }
    finally { setSaving(false); }
  };

  const addTestimonial = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/glimpses", { type: "testimonial", ...testimonial });
      setTestimonial(emptyTestimonial);
      toast.success("Testimonial added");
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add testimonial"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this item from Glimpses of OITS?")) return;
    try {
      await api.delete(`/admin/glimpses/${id}`);
      toast.success("Removed");
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to remove"); }
  };

  const photos = items.filter((x) => x.type === "photo");
  const testimonials = items.filter((x) => x.type === "testimonial");

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="text-xs uppercase tracking-widest text-accent mb-2">Admin C-Panel</div>
      <h1 className="heading-serif text-5xl mb-8">Manage Glimpses of OITS</h1>

      <Tabs defaultValue="photos">
        <TabsList className="mb-6">
          <TabsTrigger value="photos"><ImageIcon className="mr-2 h-4 w-4" /> Photos</TabsTrigger>
          <TabsTrigger value="testimonials"><MessageSquareQuote className="mr-2 h-4 w-4" /> Testimonials</TabsTrigger>
        </TabsList>

        <TabsContent value="photos">
          <form onSubmit={addPhoto} className="border border-border rounded-xl p-6 bg-card mb-8 grid md:grid-cols-[1fr_2fr_auto] gap-4 items-end">
            <div><Label>Title (optional)</Label><Input value={photo.title} onChange={(e) => setPhoto({ ...photo, title: e.target.value })} placeholder="OITS Rajasthan group tour" /></div>
            <div><Label>Photo</Label><Input required type="file" accept="image/*" onChange={(e) => setPhoto({ ...photo, file: e.target.files?.[0] || null })} className="cursor-pointer file:mr-4 file:rounded-md file:border-0 file:bg-muted file:px-3 file:py-1 file:text-sm" /></div>
            <Button disabled={saving || !photo.file} className="rounded-full bg-accent text-accent-foreground"><Plus className="h-4 w-4 mr-2" /> Add photo</Button>
          </form>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {photos.map((item) => (
              <div key={item.id} className="border border-border rounded-xl overflow-hidden bg-card">
                <img src={`${process.env.REACT_APP_BACKEND_URL}${item.image_url}`} alt={item.title || "OITS"} className="w-full aspect-[4/3] object-cover" />
                <div className="p-4 flex items-center justify-between gap-3">
                  <div className="text-sm font-medium truncate">{item.title || "OITS travel moment"}</div>
                  <Button variant="ghost" size="icon" onClick={() => remove(item.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="testimonials">
          <form onSubmit={addTestimonial} className="border border-border rounded-xl p-6 bg-card mb-8 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Traveller name</Label><Input required value={testimonial.name} onChange={(e) => setTestimonial({ ...testimonial, name: e.target.value })} /></div>
              <div><Label>Destination / trip (optional)</Label><Input value={testimonial.destination} onChange={(e) => setTestimonial({ ...testimonial, destination: e.target.value })} /></div>
            </div>
            <div><Label>Testimonial</Label><Textarea required rows={5} value={testimonial.testimonial} onChange={(e) => setTestimonial({ ...testimonial, testimonial: e.target.value })} /></div>
            <Button disabled={saving} className="rounded-full bg-accent text-accent-foreground"><Plus className="h-4 w-4 mr-2" /> Add testimonial</Button>
          </form>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((item) => (
              <div key={item.id} className="border border-border rounded-xl p-6 bg-card">
                <p className="text-muted-foreground leading-relaxed">“{item.testimonial}”</p>
                <div className="mt-6 flex items-start justify-between gap-3">
                  <div><div className="font-medium">{item.name}</div>{item.destination && <div className="text-xs text-muted-foreground mt-1">{item.destination}</div>}</div>
                  <Button variant="ghost" size="icon" onClick={() => remove(item.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
